import React from "react";
import { Button, Card, Container } from "../../components/atoms";

// ─── DESIGN SYSTEM — Tiny reusable pieces using exact Solveesy palette ───────

const Tag = ({ children }: { children: React.ReactNode }) => (
    <span className="inline-block bg-[#EEF4FF] text-[#015CFD] text-[11px] font-bold px-3 py-1 rounded-full uppercase tracking-widest">
        {children}
    </span>
);

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
    <p className="text-[11px] font-bold tracking-[2px] uppercase text-[#686868] mb-1">
        {children}
    </p>
);

const H2 = ({
    children,
    style,
}: {
    children: React.ReactNode;
    style?: React.CSSProperties;
})  => (
    <h2 className="text-[#000000] font-bold text-[22px] md:text-[28px] mt-12 mb-4 pb-3 border-b-2 border-[#000000] leading-snug" style={style}>
        {children}
    </h2>
);

const Body = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => (
    <p className={`text-[15px] text-[#464646] leading-[1.78] mb-5 font-normal ${className}`}>
        {children}
    </p>
);

const PullQuote = ({ children }: { children: React.ReactNode }) => (
    <div className="border-l-4 border-[#015CFD] bg-[#EEF4FF] rounded-r-lg px-6 py-5 my-8">
        <p className="text-[#015CFD] text-[16px] md:text-[18px] italic leading-[1.6] m-0 font-semibold">
            {children}
        </p>
    </div>
);

export const InfographicCard = ({
    label,
    title,
    children,
}: {
    label?: string;
    title?: string;
    children?: React.ReactNode;
}) => (
    <Card className="my-8 border-[#E3E4F1]">
        <SectionLabel>{label}</SectionLabel>
        <h3 className="text-[#000000] font-bold text-[18px] md:text-[22px] mb-6 leading-snug">
            {title}
        </h3>
        {children}
    </Card>
);

// ─── INFOGRAPHIC 1 — Recovery Instruments Comparison ────────────────────────
export const RecoveryInstruments = () => {
    const instruments = [
    {
        number: "01",
        title: "SARFAESI",
        act: "The Securitisation and Reconstruction of Financial Assets and Enforcement of Security Interest Act, 2002",
        subtitle: "ENFORCEMENT WITHOUT COURT",
        points: [
            "Account classified NPA under RBI norms",
            "60-day demand notice to borrower",
            "Possession of secured assets if unpaid",
            "Sale, lease, or assignment to recover dues",
            "Residue paid to borrower after recovery"
        ]
    },
    {
        number: "02",
        title: "DRT Proceedings",
        act: "The Recovery of Debts and Bankruptcy Act, 1993",
        subtitle: "TRIBUNAL ADJUDICATION",
        points: [
            "Application filed above prescribed threshold",
            "Summons issued; 30 days to show cause",
            "Compromise recorded under Section 19(20A)",
            "Bank may withdraw to pursue SARFAESI"
        ]
    },
    {
        number: "03",
        title: "OTS Mechanism",
        act: "Part of the Securitisation and Reconstruction of Financial Assets and Enforcement of Security Interest Act, 2002",
        subtitle: "NEGOTIATED EXIT",
        points: [
            "Competent authority approves compromise",
            "Deferred OTS now classified as restructuring",
            "No-dues certificate issued on full payment",
            "DRT records compromise; proceedings closed"
        ]
    },
    {
        number: "04",
        title: "MSME Recovery Procedure",
        act: "MSMED Act, 2006 · RBI Directions",
        subtitle: "MODIFIED SARFAESI OVERLAY",
        points: [
            "Below-threshold exposure: relaxed restructuring",
            "GST registration required for OTS eligibility",
            "Extended monitoring before classification upgrade",
            "MSEFC for buyer-side disputes (MSME as creditor)"
        ]
    }
];

    return (
       <InfographicCard
    label="The NPA Recovery Framework"
    title="India's Four Recovery Instruments"
>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {instruments.map((inst, i) => (
            <div
                key={i}
                className="bg-[#F8F9FA] rounded-lg p-5 border-l-4 border-[#015CFD]"
            >
                <div className="flex gap-3 mb-3">
                    <div className="text-2xl font-bold text-[#015CFD]">
                        {inst.number}
                    </div>

                    <div>
                        <h4 className="text-[15px] font-bold text-[#101010]">
                            {inst.title}
                        </h4>

                        {/* Add Act here */}
                        <p className="text-[10px] text-[#686868] leading-[1.4] mt-1">
                            {inst.act}
                        </p>

                        <p className="text-[11px] text-[#015CFD] font-semibold uppercase mt-2">
                            {inst.subtitle}
                        </p>
                    </div>
                </div>

                <ul className="space-y-1.5">
                    {inst.points.map((point, j) => (
                        <li
                            key={j}
                            className="flex gap-2 text-[13px] text-[#464646]"
                        >
                            <span className="text-[#015CFD] font-bold min-w-fit">
                                •
                            </span>
                            <span>{point}</span>
                        </li>
                    ))}
                </ul>
            </div>
        ))}
    </div>
</InfographicCard>
    );
};

// ─── INFOGRAPHIC 2 — OTS Process Timeline ────────────────────────────────────
const OTSTimeline = () => {
  const steps = [
    {
        number: "1",
        title: "Account classified NPA",
        description:
            "Mohan Traders' loan account classified non-performing after payment irregularities following the founder's death in 2009.",
        color: "bg-[#F8F9FA]"
    },
    {
        number: "2",
        title: "DRT application filed by UCO Bank",
        description:
            "UCO Bank files recovery application before DRT Jabalpur under the RDB Act, 1993.",
        color: "bg-[#F8F9FA]"
    },
    {
        number: "3",
        title: "OTS negotiated and approved",
        description:
            "Bank's competent authority approves a compromise. Clause 9.1.9 certifies: no documentation lapses, no irregularities per legal audit.",
        color: "bg-[#EEF4FF]"
    },
    {
        number: "4",
        title: "Settlement amount paid in full",
        description:
            "Borrower pays the agreed OTS amount. No-dues certificate issued. Joint application filed before DRT.",
        color: "bg-[#EEF4FF]"
    },
    {
        number: "5",
        title: "DRT records compromise and closes",
        description:
            "Under Section 19(20A), Tribunal records the settlement. Original application disposed as withdrawn. Proceedings formally closed.",
        color: "bg-[#EEF4FF]"
    },
    {
        number: "6",
        title: "Criminal complaint filed — Supreme Court intervenes",
        description:
            "Bank files subsequent criminal complaint. Supreme Court holds: a DRT-endorsed settlement, honestly entered and fully performed, is genuinely and finally closed.",
        color: "bg-[#E8F5EF]"
    }
];

    return (
        <InfographicCard label="Case Background · Mohan Traders" title="The OTS process adopted in the case">
            <div className="flex flex-col">
                {steps.map((step, i) => (
                    <div key={i} className="flex gap-4 pb-6 relative">
                        {i < steps.length - 1 && (
                            <div className="absolute left-[18px] top-[40px] bottom-0 w-[2px] bg-[#E3E4F1]" />
                        )}
                        <div className={`w-[38px] h-[38px] min-w-[38px] rounded-full flex items-center justify-center font-bold text-[13px] z-10 border-2 border-[#015CFD] ${step.color}`}>
                            {step.number}
                        </div>
                        <div className="pt-1">
                            <div className="text-[15px] font-semibold text-[#101010] mb-1">{step.title}</div>
                            <p className="text-[13.5px] text-[#464646] leading-[1.55] m-0 font-normal">{step.description}</p>
                        </div>
                    </div>
                ))}
            </div>
        </InfographicCard>
    );
};

// ─── INFOGRAPHIC 3 — Supreme Court Findings ──────────────────────────────────
const CourtFindings = () => {
   const findings = [
    {
        number: "1",
        title: "A DRT-endorsed settlement carries judicial weight",
        content:
            "The Court drew a clear distinction between a private OTS and a settlement recorded and endorsed by the DRT as a judicial forum. When both parties file a joint application, the Tribunal notes the compromise, and proceedings are formally withdrawn after full payment — that settlement has, in the Court's words, 'the imprimatur of the DRT. Permitting criminal prosecution after such a settlement would 'adversely impact the sanctity of such settlement which has become part of the judicial proceeding.' The DRT's endorsement is a legal shield — for the borrower who has performed, and for the lender who has certified."
    },
    {
        number: "2",
        title: "What was certified at settlement binds the certifying party",
        content:
            "The Court placed significant weight on Clause 9.1.9 — the bank's own certification that there were no documentation lapses and no irregularities per legal audit. Having made that representation in the settlement terms, and having received full consideration on the basis of it, the bank could not subsequently allege the opposite in a criminal complaint. The settlement deed is not boilerplate. Every clause that certifies the state of documentation, collateral, and compliance is a legal representation that binds the institution going forward."
    },
    {
        number: "3",
        title: "The systemic and economic dimension",
        content:
            "If criminal prosecution after a DRT-endorsed settlement is permitted, 'many persons including commercial entities would be hesitant to come forward and seek resolution of their disputes.' The downstream effect would be 'debilitating on the overall economy.' The OTS mechanism — which resolves significant volumes of stressed assets every year — functions because borrowers trust that settlement means finality. Judicial erosion of that trust affects not individual accounts but the willingness of an entire class of borrowers to engage with the resolution process at all."
    }
];
    

    return (
        <InfographicCard label="" title="">
            <div className="space-y-4">
                {findings.map((f, i) => (
                    <div key={i} className="bg-[#EEF4FF] border-l-4 border-[#015CFD] rounded-r-lg p-5">
                        <div className="flex gap-3 mb-2">
                            <div className="text-2xl font-bold text-[#015CFD] leading-none">{f.number}</div>
                            <h4 className="text-[15px] font-bold text-[#101010] pt-0.5">{f.title}</h4>
                        </div>
                        <p className="text-[13.5px] text-[#464646] leading-[1.6] m-0">{f.content}</p>
                    </div>
                ))}
            </div>
        </InfographicCard>
    );
};

// ─── INFOGRAPHIC 4 — Pre-Settlement Checkpoints ────────────────────────────
const PreSettlementCheckpoints = () => {
 const checkpoints = [
    {
        icon: "✓",
        title: "Legal audit completion and sign-off",
        description:
            "The legal audit must be completed and formally signed off before the compromise proposal is drafted — not after. The finding becomes the basis of the certification made in the deed."
    },
    {
        icon: "✓",
        title: "Fraud assessment and vigilance clearance",
        description:
            "Fraud assessment and vigilance clearance must be a formal condition of settlement approval — documented in the approval file, not assumed. If fraud concerns exist, they must be investigated and their outcome recorded before the compromise is entered."
    },
    {
        icon: "✓",
        title: "Explicit documentation within the settlement deed",
        description:
            "What was reviewed and what was found must be explicitly documented inside the settlement deed itself — not held in a separate file that may surface inconsistently later. The sequence matters as much as the substance."
    }
];

    return (
        <InfographicCard label="" title="Pre-settlement workflow — mandatory sequence">
            <div className="space-y-4">
                {checkpoints.map((cp, i) => (
                    <div
                        key={i}
                        className="bg-white rounded-xl border border-[#00000020] p-5"
                    >

          
                        <h4 className="text-[15px] font-bold text-[#101010] mb-2">
                            {cp.title}
                        </h4>

                        <p className="text-[13px] text-[#464646] leading-[1.6]">
                            {cp.description}
                        </p>
                    </div>
                ))}
            </div>
        </InfographicCard>
    );
};

// ─── INFOGRAPHIC 5 — Solveesy Solution ───────────────────────────────────────
const SolveesySolution = () => {
    const features = [
        {
            icon: "🏛️",
            title: "Structured closure checkpoints",
            description: "Pre-settlement workflows structured around checkpoints mandated by Supreme Court — in the right sequence."
        },
        {
            icon: "📊",
            title: "Documented audit trail",
            description: "Every settlement built on a documented audit trail — from first classification through every intervention to final closure."
        },
        {
            icon: "🤝",
            title: "Integrated platform",
            description: "Tele-calling, field verification, and legal capability integrated in one platform."
        },
        {
            icon: "⚖️",
            title: "Regulatory defensibility",
            description: "Full audit trails and conduct standards — built for RBI inspection readiness."
        }
    ];

    return (
        <Card className="my-8 bg-gradient-to-br from-[#EEF4FF] to-[#F8FAFF] border-l-4 border-[#015CFD]   border-l-[#015CFD] rounded-2xl p-6 md:p-10 relative overflow-hidden">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,0.25),transparent_60%)] pointer-events-none" />
            <div className="relative">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {features.map((f, i) => (
                        <div key={i} className="bg-white/60 backdrop-blur-sm border border-[#015CFD]/15 rounded-xl p-5">
                            <div className="text-3xl mb-2">{f.icon}</div>
                            <h4 className="text-[14px] font-bold text-[#101010] mb-1">{f.title}</h4>
                            <p className="text-[13px] text-[#464646] leading-snug">{f.description}</p>
                        </div>
                    ))} 
                </div>
            </div>
        </Card>
    );
};

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────
export const CaseStudyBlog = () => {
    const handleScrollToFooter = () => {
        const footer = document.querySelector('footer');
        if (footer) {
            footer.scrollIntoView({ behavior: "smooth" });
        }
    };

    return (
        <div className="bg-white min-h-screen">

            {/* ── HERO ── */}
            <div className="relative overflow-hidden">
                <div className="w-[403px] h-[403px] rounded-full bg-[#00C2FF] blur-[200px] absolute -z-10 opacity-25 -top-20 left-[60px] max-sm:hidden pointer-events-none" />

                <Container className="py-14 md:py-20">
                    <div className="flex flex-col items-center text-center gap-10">
                        <div className="flex flex-wrap gap-2 justify-center mb-4">
                            <Tag>2026 Collections Intelligence</Tag>
                            <Tag>NPA Settlements</Tag>
                            <Tag>Supreme Court Ruling</Tag>
                        </div>

                        <h1 className="text-[32px] md:text-[38px] font-bold leading-tight text-[#000000] mb-3 max-w-4xl">
                            When "Settled" Actually Means <span className="text-[#015CFD]">Settled</span> : The New Rulebook
for NPA Settlements
                        </h1>

                        <p className="text-[#464646] text-sm md:text-base font-normal w-full md:w-3xl leading-relaxed mb-6">
                     What every Bank, NBFC and Fintech need to know
                        </p>
                    </div>
                </Container>
            </div>

            {/* ── ARTICLE BODY ── */}
            <Container className="py-10 md:py-14">

                {/* Key Question */}
                <PullQuote>
                    When your collections team closes a settlement — compromise recorded before the DRT, no-dues certificate issued, loan account closed — is that account genuinely off your books? Or does it carry residual legal exposure that could surface years later?
                </PullQuote>

                {/* ─── Section 1: Recovery Framework ─── */}
                <H2>India's Four <span className="text-[#015CFD]">Recovery Instruments</span></H2>

                <Body>
                    Banks, NBFCs, and fintech lenders rely on four principal recovery mechanisms—often deployed in tandem—to recover stressed assets. Understanding how these mechanisms interact is key to appreciating the Supreme Court's ruling in Vijay Kumar Kela & Anr. v. CBI & Anr. and why the decision carries significant operational implications for lenders.
                </Body>

                <RecoveryInstruments />

                {/* ─── Section 2: Case Background ─── */}
                <H2><span className="text-[#015CFD]">Mohan Traders Case</span></H2>

                <Body>
                    Mohan Traders was a sole proprietorship in agricultural inputs. When its founder passed away in 2009, his brother stepped in. Deteriorating business conditions led to NPA classification. What followed became the blueprint the Supreme Court ultimately validated.
                </Body>

                <OTSTimeline />

                {/* ─── Section 3: Supreme Court Ruling ─── */}
    <span className="text-[#015CFD]" >Supreme Court Ruling · Three Findings</span>
             <H2 style={{marginTop:"10px"}}>What the  <span className="text-[#015CFD]" >Court</span> held — and what it means Banks, NBFCs and
FinTechs
</H2>
                <Body>
                    The ruling rests on three findings, each with direct operational significance for lending institutions. 
                </Body>

                <CourtFindings />

                {/* ─── Section 4: Important Boundary ─── */}
                <div className="my-12 p-6 bg-[#FEF9EC] border-l-4 border-[#B45309] rounded-r-lg">
                    <div className="flex gap-3 mb-3">
                        <span className="text-2xl">⚠️</span>
                        <h4 className="text-[15px] font-bold text-[#101010]">Important Boundary</h4>
                    </div>
                    <p className="text-[13.5px] text-[#464646] leading-[1.6] m-0">
                        Where fraud is independently established by forensic audit <strong>before settlement</strong>, and that finding is properly documented and on record prior to the compromise being entered, criminal proceedings are not extinguished by a subsequent OTS. The rule applies where the lender certifies clean documentation in the settlement terms — which represents the overwhelming majority of genuine NPA settlements.
                    </p>
                </div>

            
                <H2> <span className="text-[#015CFD]">Three checkpoints</span> every institution now needs</H2>

                <Body>
                    The judgment creates one clear obligation: everything material must be resolved before settlement, not after. These checkpoints — which may exist in your process but not necessarily in the right order — now determine whether a settlement is legally final or a liability waiting to surface.
                </Body>

                <PreSettlementCheckpoints />

                {/* ─── Section 6: Solveesy Solution ─── */}
                <H2> <span className="text-[#015CFD]">Solveesy. Collections as a Service
</span>  Built for <span className="text-[#015CFD]">Exactly This</span></H2>

                <Body>
                    At Solveesy, we build collections infrastructure specifically for banks, NBFCs, and fintechs. For your institution, a settlement is not just a financial outcome. It is a legal representation, a regulatory record, and a permanent entry in your credit history. The quality of the process that produces a settlement determines whether it is genuinely final — or a liability waiting to surface.
                </Body>

                <SolveesySolution />

                {/* ── CTA SECTION ── */}
                <Card className="relative rounded-[40px] mt-14 overflow-hidden bg-gradient-to-br from-[#EEF4FF] to-[#F8FAFF] border-l-4 border-[#015CFD]">
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,0.25),transparent_60%)] pointer-events-none" />

                    <div className="relative flex flex-col items-center justify-center text-center px-6 md:px-16 py-10 md:py-14">
                        <h2 className="text-[#000000] font-bold text-[24px] md:text-[36px] leading-tight mb-3">
                            Ready to Modernise Your <span className="text-[#015CFD]">Collections Strategy?</span>
                        </h2>

                        <p className="text-[#464646] text-[15px] md:text-[18px] leading-relaxed max-w-lg mb-8 font-normal">
                        Solveesy works with lenders across consumer finance, digital lending, and MSME credit to build compliant, early-stage collections capability.
                        </p>

                        <Button
                            type="button"
                            className="text-[#015CFD] bg-[#E7E7E7] hover:bg-[#D0D0D0] text-[16px] font-medium px-8 py-3 rounded-[64px]"
                            onClick={handleScrollToFooter}
                        >
                            Connect with Us →
                        </Button>
                    </div>
                </Card>

            </Container>
        </div>
    );
};

export default CaseStudyBlog;